import { useCallback, useState } from 'react';
import Cookies from 'js-cookie';

import BFF from '@shared/services/BFF';
import { Cookie } from '@shared/v2/constants/storage';
import { LoginEventArgs } from '@shared/v2/utils/dataLayer/eventCreators/ga4';
import { useOAuthState } from '@shared/v2/utils/oauth/useOAuthState';

import AuthContext from './AuthContext';
import { useInitialiseMemberDetails } from './hooks/useInitialiseMemberDetails';
import { useSetOAuthStatePath } from './hooks/useSetOAuthStatePath';
import { Member } from './types';

type DataLayer = {
  logoutEvent: (data: LoginEventArgs) => void;
};

export type AuthProviderProps = {
  children: React.ReactNode;
  dataLayer: DataLayer;
  config: {
    client: string;
    clientId: string;
    redirectUri: string;
    qffAuthApiUrl: string;
    lslAuthApiUrl: string;
  };
};

export const QFF_HASH_COOKIE_KEY = 'QF_VALUE';

const AuthProvider = ({ dataLayer, config, children }: AuthProviderProps) => {
  // Initialised as undefined, and will be Member | null after "sync"
  const [memberDetails, setMemberDetails] = useState<Member | undefined | null>(
    undefined,
  );
  const isMemberDetailsReady = memberDetails !== undefined;
  const [oAuthState] = useOAuthState();
  const encodedOAuthState = encodeURIComponent(oAuthState);
  const { clientId, lslAuthApiUrl, qffAuthApiUrl, redirectUri } = config;

  const login = useCallback(() => {
    const hasSession = typeof Cookies.get(Cookie.QL_WL_SESSION) === 'string';
    const oAuthURL = `${lslAuthApiUrl}/oauth?response_type=code&client_id=${clientId}&scope=profile&redirect_uri=${redirectUri}/auth&web=true&state=${encodedOAuthState}`;

    window.location.href = hasSession ? `${oAuthURL}&prompt=none` : oAuthURL;
  }, [clientId, encodedOAuthState, lslAuthApiUrl, redirectUri]);

  const logout = async () => {
    try {
      await fetch(`${qffAuthApiUrl}/holidays/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_) {
      // Swallow any error and continue to logoff.
    } finally {
      Cookies.remove(Cookie.LSL_USER_ID);
      Cookies.remove(Cookie.QH_SESS);
    }

    try {
      await fetch(`${lslAuthApiUrl}/logoff`, {
        credentials: 'include',
      });
    } catch (_) {
      // Swallow any error and continue to logoff.
    } finally {
      Cookies.remove(Cookie.QL_WL_SESSION);
    }

    setMemberDetails(null);

    const qffHash = Cookies.get(QFF_HASH_COOKIE_KEY);
    dataLayer.logoutEvent({
      qffHash,
      points: memberDetails?.points,
      loginStatus: 'logged out',
    });
  };

  const syncMemberDetails = useCallback(async () => {
    const hasLSLUserId = typeof Cookies.get(Cookie.LSL_USER_ID) === 'string';
    if (!hasLSLUserId) {
      setMemberDetails(null);
      return;
    }

    try {
      const detail: Member | undefined = await BFF.getMember()();
      if (detail?.memberId && Object.keys(detail).length > 1) {
        setMemberDetails(detail);
      } else {
        setMemberDetails(null);
      }
    } catch (_) {
      setMemberDetails(null);
    }
  }, []);

  useSetOAuthStatePath({ memberDetails, oAuthState });
  useInitialiseMemberDetails({
    memberDetails,
    syncMemberDetails,
    setMemberDetails,
  });

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        syncMemberDetails,
        memberDetails,
        isMemberDetailsReady,
        qffAuthApiUrl,
        clientId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
